import { type ChangeEvent, useEffect, useRef } from 'react';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { Loader2 } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-field';
import { SimpleSelect } from '@/components/ui/simple-select';
import { PrefixedInput } from '@/components/ui/prefixed-input';
import { TermsInfo } from './TermsInfo';
import { useRegisterMutation } from '../services/registrationApi';
import { useDropdownOptions } from '../../properties/services/useDropdownOptions';
import { useNavigationService } from '../../../services/navigate-service';
import { showAlert } from '../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import {
  formatPhoneNumberInField,
  hasNoUrlUserInfo,
  stripUrlProtocol,
  toHttpsUrl,
} from '../../../util/formatting';
import { getFieldError } from '../utils/fieldVisibility';
import type { RegisterFormValues } from '../../../types/registration.types';

interface PersonalDetailsFormProps {
  onSuccess: (result: { registered: boolean; userId: string; email: string }) => void;
}

const schema = yup.object().shape({
  firstName: yup
    .string()
    .required('Please enter first name')
    .matches(
      /^[a-zA-Z.,' -]*$/,
      'Please enter letters, spaces, hyphen, period, comma and apostrophe only',
    ),
  lastName: yup
    .string()
    .required('Please enter last name')
    .matches(
      /^[a-zA-Z.,' -]*$/,
      'Please enter letters, spaces, hyphen, period, comma and apostrophe only',
    ),
  email: yup
    .string()
    .matches(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      'Email address is incorrect, please enter a valid email address',
    )
    .required('Please enter email'),
  phone: yup
    .string()
    .required('Please enter your phone number')
    .test('is-valid-phone', 'Please enter a valid 10-digit phone number', (value) => {
      if (!value) return false;
      const digits = value.replace(/\D/g, '');
      return (
        (digits.length === 10 && !digits.startsWith('1')) ||
        (digits.length === 11 && digits.startsWith('1'))
      );
    }),
  properties: yup.string().required('Please select an option from the above list.'),
  companyName: yup.string().required('Please enter company name'),
  website: yup
    .string()
    .transform((value) => toHttpsUrl(value))
    .required('Please enter website')
    .url('Please enter a valid website URL')
    .test('no-userinfo', 'Please enter a valid website URL', hasNoUrlUserInfo),
});

const initialValues: RegisterFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  properties: '',
  companyName: '',
  website: '',
};

const PersonalDetailsForm = ({ onSuccess }: PersonalDetailsFormProps) => {
  const dispatch = useDispatch();
  const { navigateTo } = useNavigationService();
  const propertiesRef = useRef<{ clearValue: () => void }>(null);
  const [register, { isLoading }] = useRegisterMutation();
  const propertiesOwnedOptions = useDropdownOptions('propertyOwned', 'entity');

  useEffect(() => {
    document.title = 'PlanOmatic - Register';
    return () => {
      document.title = 'PlanOmatic';
    };
  }, []);

  const handleSubmit = async (
    values: RegisterFormValues,
    setFieldError: (field: string, message: string) => void,
    resetForm: () => void,
  ) => {
    try {
      const response = await register(values).unwrap();
      resetForm();
      propertiesRef.current?.clearValue();
      onSuccess({ registered: true, userId: response.userId, email: values.email });
    } catch (error: unknown) {
      const apiError = error as { data?: { message?: string } };
      const msg = apiError?.data?.message ?? '';
      if (msg.includes('USER_ALREADY_EXISTS')) {
        setFieldError('email', 'Email already in use. Please choose different email.');
      } else if (msg.includes('Entity with the same name already exists')) {
        setFieldError(
          'companyName',
          'A company with the same name already exists. Please contact your administrator or support@planomatic.com for assistance.',
        );
      } else {
        dispatch(showAlert({ type: Variants.ERROR, message: 'Something went wrong, Please contact admin' }));
      }
    }
  };

  return (
    <Formik<RegisterFormValues>
      validationSchema={schema}
      initialValues={initialValues}
      validateOnBlur
      validateOnChange={false}
      onSubmit={(values, { setFieldError, resetForm }) =>
        handleSubmit(values, setFieldError, resetForm)
      }
    >
      {({
        handleSubmit: onSubmit,
        handleBlur,
        values,
        touched,
        errors,
        setFieldValue,
        submitCount,
      }) => {
        const firstNameError = getFieldError(touched.firstName, values.firstName, errors.firstName, submitCount);
        const lastNameError = getFieldError(touched.lastName, values.lastName, errors.lastName, submitCount);
        const emailError = getFieldError(touched.email, values.email, errors.email, submitCount);
        const phoneError = getFieldError(touched.phone, values.phone, errors.phone, submitCount);
        const companyNameError = getFieldError(
          touched.companyName,
          values.companyName,
          errors.companyName,
          submitCount,
        );
        const websiteError = getFieldError(touched.website, values.website, errors.website, submitCount);
        const propertiesError = getFieldError(
          touched.properties,
          values.properties,
          errors.properties,
          submitCount,
        );

        return (
        <Form
          id="register-form"
          data-testid="register-form"
          noValidate
          onSubmit={onSubmit}
          className="space-y-5"
        >
          {/* Name row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first-name" required>First Name</Label>
              <Input
                id="first-name"
                name="firstName"
                placeholder="Enter First Name"
                maxLength={35}
                value={values.firstName}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('firstName', e.target.value, touched.firstName)
                }
                onBlur={handleBlur}
                className={firstNameError ? 'border-destructive' : ''}
                data-testid="first-name_id"
              />
              <FormError message={firstNameError} />
            </div>
            <div>
              <Label htmlFor="last-name" required>Last Name</Label>
              <Input
                id="last-name"
                name="lastName"
                placeholder="Enter Last Name"
                maxLength={35}
                value={values.lastName}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('lastName', e.target.value, touched.lastName)
                }
                onBlur={handleBlur}
                className={lastNameError ? 'border-destructive' : ''}
                data-testid="last-name_id"
              />
              <FormError message={lastNameError} />
            </div>
          </div>

          {/* Email + Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Email */}
            <div>
              <Label htmlFor="email" required>Email</Label>
              <Input
                id="email"
                name="email"
                placeholder="Enter Email"
                maxLength={320}
                value={values.email}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('email', e.target.value, touched.email)
                }
                onBlur={handleBlur}
                className={emailError ? 'border-destructive' : ''}
                data-testid="email_id"
              />
              <FormError message={emailError} />
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phoneNumber" required>Phone</Label>
              <Input
                id="phoneNumber"
                name="phone"
                placeholder="Enter Phone Number"
                value={values.phone}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('phone', formatPhoneNumberInField(e.target.value), touched.phone)
                }
                onBlur={handleBlur}
                className={phoneError ? 'border-destructive' : ''}
                data-testid="phoneNumber_id"
              />
              <FormError message={phoneError} />
            </div>
          </div>

          {/* Company name */}
          <div>
            <Label htmlFor="company-name" required>Company Name</Label>
            <Input
              id="company-name"
              name="companyName"
              placeholder="Enter Company Name"
              maxLength={160}
              value={values.companyName}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setFieldValue('companyName', e.target.value, touched.companyName)
              }
              onBlur={handleBlur}
              className={companyNameError ? 'border-destructive' : ''}
              data-testid="company-name_id"
            />
            <FormError message={companyNameError} />
          </div>

          {/* Website */}
          <div>
            <Label htmlFor="website" required>Website</Label>
            <PrefixedInput
              id="website"
              name="website"
              prefix="https://"
              placeholder="example.com"
              maxLength={255}
              value={values.website}
              onChange={(e) =>
                setFieldValue('website', stripUrlProtocol(e.target.value), touched.website)
              }
              onBlur={handleBlur}
              className={websiteError ? 'border-destructive' : ''}
              data-testid="website_id"
            />
            <FormError message={websiteError} />
          </div>

          {/* Properties owned */}
          <div>
            <SimpleSelect
              options={propertiesOwnedOptions}
              name="properties"
              md="12"
              controlId="properties"
              ariaDescribedBy="properties-select"
              placeholder="Select"
              ref={propertiesRef}
              label="How Many Units Do You Manage?"
              errors={propertiesError}
              setFieldValue={setFieldValue}
              additionalData={touched.properties}
              isRequired
              field="properties"
            />
          </div>

          {/* Terms */}
          <div>
            <TermsInfo />
          </div>

          {/* Submit */}
          <div>
            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading && <Loader2 className="animate-spin" />}
              Sign Up
            </Button>
          </div>

          {/* Sign in link — sized to match the sign-in page's "Sign Up" footer (16px) */}
          <div className="text-center text-base">
            Already have an account?{' '}
            <Button
              type="button"
              variant="link"
              disabled={isLoading}
              className="text-base"
              // Inline prose link ("Already have an account? …") — exempt
              // from the 44px floor per WCAG 2.5.8's inline exception; the
              // e2e tap-target sweep allowlists it by this testid.
              data-testid="sign-in-inline-link"
              onClick={() => {
                sessionStorage.clear();
                navigateTo('/');
              }}
            >
              Sign In
            </Button>
          </div>
        </Form>
        );
      }}
    </Formik>
  );
};

export default PersonalDetailsForm;
